import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import { InformationCollapsible } from '@dndbeyond/character-common-components/es';
import {
    HtmlContent,
    DdbBadgeSvg,
    DisabledChevronDownSvg,
    RacialTraitsShortDescription,
} from '@dndbeyond/character-components/es';
import {
    rulesEngineSelectors,
    characterActions,
    Constants,
    DefinitionUtils,
    Feat,
    FeatUtils,
    Race,
    RaceDefinitionContract,
    RaceUtils,
    RacialTraitContract,
    RacialTraitUtils,
    RuleData,
    RuleDataUtils,
    serviceDataSelectors,
    DefinitionPool,
} from '@dndbeyond/character-rules-engine/es';

import { modalActions } from '../../../../Shared/actions';
import { FullscreenModal } from '../../../../Shared/components/common/FullscreenModal';
import { Collapsible } from '../../../../Shared/components/legacy/common/Collapsible';
import { modalSelectors } from '../../../../Shared/selectors';
import { TypeScriptUtils } from '../../../../Shared/utils';
import PageHeader from '../../../components/PageHeader';
import { builderSelectors } from '../../../selectors';

interface RacialTraitProps {
    racialTrait: RacialTraitContract;
}
class RaceConfirmRacialTrait extends React.PureComponent<RacialTraitProps> {
    render() {
        const { racialTrait } = this.props;

        const description = RacialTraitUtils.getDescription(racialTrait);
        const name = RacialTraitUtils.getName(racialTrait);

        return (
            <Collapsible trigger={name} clsNames={['race-detail-racial-trait-name']} initiallyCollapsed={false}>
                <HtmlContent className="race-detail-racial-trait-description" html={description ? description : ''} />
            </Collapsible>
        );
    }
}

interface RaceChooseContentProps {
    race: RaceDefinitionContract;
    ruleData: RuleData;
    definitionPool: DefinitionPool;
}
class RaceChooseContent extends React.PureComponent<RaceChooseContentProps> {
    render() {
        const { race, ruleData } = this.props;

        const fullName = RaceUtils.getFullName(race);
        const portraitAvatarUrl = RaceUtils.getPortraitAvatarUrl(race);
        const description = RaceUtils.getDescription(race);
        const moreDetailsUrl = RaceUtils.getMoreDetailsUrl(race);
        const racialTraits = RaceUtils.getDefinitionRacialTraits(race);
        const simulatedRacialTraits = racialTraits
            .map((racialTrait) =>
                RacialTraitUtils.simulateRacialTraitFromContract(racialTrait),
            );
        const calledOutRacialTraits = RaceUtils.deriveCalledOutRacialTraits(simulatedRacialTraits, ruleData);
        // TODO eventually we should simulate a race, but not going to add that at this point
        // TODO eventually pull in RacialTraitsShortDescription once race or racialTraits can be fully simulated with modifiers
        let orderedRacialTraits: Array<RacialTraitContract> = racialTraits
            ? RaceUtils.deriveOrderedRacialTraits(racialTraits)
            : [];
        let visibleRacialTraits: Array<RacialTraitContract> = RaceUtils.deriveVisibleRacialTraits(
            orderedRacialTraits,
            Constants.AppContextTypeEnum.BUILDER,
        );
        visibleRacialTraits = RacialTraitUtils.filterRacialTraitsByDisplayConfigurationType(
            visibleRacialTraits,
            Constants.DisplayConfigurationTypeEnum.RACIAL_TRAIT,
        );

        const definitionKey = DefinitionUtils.hack__generateDefinitionKey(
            RaceUtils.getEntityRaceTypeId(race),
            RaceUtils.getEntityRaceId(race),
        );
        const builderText = RuleDataUtils.getBuilderHelperTextByDefinitionKey(
            definitionKey,
            ruleData,
            Constants.DisplayConfigurationTypeEnum.RACIAL_TRAIT,
        );

        const previewUrl: string | null = portraitAvatarUrl ?? RuleDataUtils.getDefaultRaceImageUrl(ruleData);

        const descriptionClassNames: Array<string> = ['race-detail-description'];

        if (calledOutRacialTraits.length > 0) {
            descriptionClassNames.push('race-detail-description--hide-traits');
        }

        return (
            <div className="confirm-race race-detail">
                <div className="race-detail-primary">
                    <div className="race-detail-aside">
                        <div className="race-detail-preview">
                            <img className="race-detail-preview-img" src={previewUrl ? previewUrl : ''} alt="" />
                        </div>
                    </div>
                    <PageHeader>{fullName}</PageHeader>
                    <HtmlContent className={descriptionClassNames.join(' ')} html={description ? description : ''} />
                    {calledOutRacialTraits.length > 0 && (
                        <RacialTraitsShortDescription calledOutRacialTraits={calledOutRacialTraits} />
                    )}
                    <div className="race-detail-more">
                        <a className="race-detail-more-link" href={moreDetailsUrl ? moreDetailsUrl : ''}>
                            {fullName} Details Page
                        </a>
                    </div>
                </div>
                <div className="race-detail-secondary">
                    {builderText.map((helperText) => (
                        <InformationCollapsible
                            title={helperText.label}
                            description={helperText.description}
                            headerIcon={<DdbBadgeSvg />}
                            collapsibleIcon={<DisabledChevronDownSvg />}
                        />
                    ))}
                    {visibleRacialTraits.length > 0 && (
                        <div className="race-detail-racial-traits">
                            {visibleRacialTraits.map((racialTrait) => (
                                <RaceConfirmRacialTrait
                                    key={RacialTraitUtils.getId(racialTrait)}
                                    racialTrait={racialTrait}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

interface Props extends DispatchProp {
    race: Race | null;
    feats: Array<Feat>;
    ruleData: RuleData;
    confirmRace: RaceDefinitionContract | null;
    isOpen: boolean;
    modalKey: string;
    definitionPool: DefinitionPool;
}
class RaceChooseConfirm extends React.PureComponent<Props> {
    handleCancelModal = (): void => {
        const { dispatch, modalKey } = this.props;

        dispatch(modalActions.close(modalKey));
    };

    handleAcceptModal = (): void => {
        const { dispatch, modalKey, confirmRace } = this.props;

        if (confirmRace) {
            dispatch(characterActions.raceChoose(confirmRace));
        }
        dispatch(modalActions.close(modalKey));
    };

    render() {
        const { isOpen, confirmRace, race, feats, ruleData, definitionPool } = this.props;

        let missingFeatDependencies: Array<Feat> = [];
        if (race && confirmRace) {
            let raceFeatIds = RaceUtils.getFeatIds(race);
            let confirmRaceFeatIds = RaceUtils.getFeatIds(confirmRace);
            feats.forEach((feat) => {
                if (
                    raceFeatIds.includes(FeatUtils.getId(feat)) &&
                    !confirmRaceFeatIds.includes(FeatUtils.getId(feat))
                ) {
                    missingFeatDependencies.push(feat);
                }
            });
        }

        const acceptChangesText = race ? 'Change Race' : 'Choose Race';
        const heading = race ? 'Confirm Change Race' : 'Confirm Race';

        return (
            <FullscreenModal
                clsNames={['race-confirm-modal']}
                onCancel={this.handleCancelModal}
                onAccept={this.handleAcceptModal}
                isOpen={isOpen}
                acceptChangesText={acceptChangesText}
                heading={heading}
            >
                {missingFeatDependencies.length > 0 && (
                    <div className="builder-message builder-message-warning">
                        By changing your race, you will have invalid feat selection(s):
                        {' ' + missingFeatDependencies.map((feat) => FeatUtils.getName(feat)).join(', ')}
                    </div>
                )}
                {confirmRace && (
                    <RaceChooseContent race={confirmRace} ruleData={ruleData} definitionPool={definitionPool} />
                )}
            </FullscreenModal>
        );
    }
}

function mapStateToProps(state) {
    const modalKey = 'race-choose-confirm';

    return {
        modalKey,
        isOpen: modalSelectors.getOpenStatus(state, modalKey),
        confirmRace: builderSelectors.getConfirmRace(state),
        race: rulesEngineSelectors.getRace(state),
        feats: rulesEngineSelectors.getFeats(state),
        ruleData: rulesEngineSelectors.getRuleData(state),
        definitionPool: serviceDataSelectors.getDefinitionPool(state),
    };
}

export default connect(mapStateToProps)(RaceChooseConfirm);
